import { useState, useEffect } from 'react';

interface HeroProps {
  onContactClick: () => void;
}

export default function Hero({ onContactClick }: HeroProps) {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const backgroundImages = [
    'https://public.readdy.ai/ai/img_res/43f8df9e32c49b3a2cbbecc12d96607a.jpg',
    'https://readdy.ai/api/search-image?query=elegant%20law%20office%20interior%20with%20rich%20wood%20paneling%20leather%20furniture%20sophisticated%20legal%20workspace%20professional%20attorney%20office%20design%20executive%20meeting%20room%20refined%20business%20environment%20luxury%20legal%20office&width=1920&height=1080&seq=hero-lawoffice-2&orientation=landscape',
    'https://readdy.ai/api/search-image?query=modern%20courtroom%20interior%20with%20wooden%20benches%20judge%20bench%20witness%20stand%20professional%20legal%20setting%20contemporary%20justice%20system%20architecture%20formal%20court%20proceedings%20room%20judicial%20chamber%20design&width=1920&height=1080&seq=hero-courtroom-3&orientation=landscape',
    'https://readdy.ai/api/search-image?query=professional%20legal%20documents%20on%20desk%20with%20gavel%20scales%20of%20justice%20law%20books%20elegant%20office%20setting%20court%20reporting%20equipment%20stenography%20machine%20legal%20paperwork%20sophisticated%20workspace%20attorney%20desk&width=1920&height=1080&seq=hero-legal-desk-4&orientation=landscape'
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prevIndex) => (prevIndex + 1) % backgroundImages.length);
    }, 4000);

    return () => clearInterval(interval);
  }, []);

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Background Images with Transition */}
      {backgroundImages.map((image, index) => (
        <div
          key={index}
          className={`absolute inset-0 transition-opacity duration-1000 ${
            index === currentImageIndex ? 'opacity-100' : 'opacity-0'
          }`}
        >
          <img
            src={image}
            alt=""
            className="w-full h-full object-cover object-top"
          />
        </div>
      ))}

      {/* Dark Overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 py-32 text-center w-full">
        {/* Trust Badge */}
        <div className="inline-flex items-center bg-white px-6 py-3 rounded-full mb-8 shadow-2xl border-4 border-[#FF6B6B] animate-pulse">
          <div className="w-8 h-8 flex items-center justify-center mr-3">
            <i className="ri-trophy-line text-2xl text-[#FFD700]"></i>
          </div>
          <span className="text-[#1A2332] font-bold text-lg">Trusted by</span>
          <span className="text-[#FF6B6B] font-black text-3xl mx-2">200+</span>
          <span className="text-[#1A2332] font-bold text-lg">Court Reporters</span>
        </div>

        <h1 className="font-['Playfair_Display'] text-6xl md:text-7xl lg:text-8xl font-bold text-white mb-6 leading-tight">
          Professional Websites for<br />
          <span className="text-[#FF6B6B]">Court Reporters</span>
        </h1>
        
        <p className="text-xl md:text-2xl text-white/95 mb-12 max-w-3xl mx-auto leading-relaxed font-medium">
          Stand out from the competition with a stunning, SEO-optimized website that attracts more clients and grows your business
        </p>

        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-16">
          <button
            onClick={onContactClick}
            className="bg-[#FF6B6B] text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-[#ff5252] transition-all shadow-xl hover:shadow-2xl hover:scale-105 whitespace-nowrap cursor-pointer"
          >
            Get Your Free Quote
            <i className="ri-arrow-right-line ml-2"></i>
          </button>
          <button
            onClick={onContactClick}
            className="bg-white text-[#1A2332] px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-100 transition-all shadow-xl hover:shadow-2xl hover:scale-105 whitespace-nowrap cursor-pointer"
          >
            View Our Work
            <i className="ri-eye-line ml-2"></i>
          </button>
        </div>

        {/* Features Grid */}
        <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
          <div className="bg-white/95 backdrop-blur-sm p-6 rounded-xl shadow-xl">
            <div className="w-14 h-14 bg-[#FF6B6B]/10 rounded-lg flex items-center justify-center mx-auto mb-4">
              <i className="ri-smartphone-line text-3xl text-[#FF6B6B]"></i>
            </div>
            <h3 className="font-bold text-[#1A2332] text-lg mb-2">Mobile Responsive</h3>
            <p className="text-gray-600 text-sm">Perfect on all devices</p>
          </div>

          <div className="bg-white/95 backdrop-blur-sm p-6 rounded-xl shadow-xl">
            <div className="w-14 h-14 bg-[#FF6B6B]/10 rounded-lg flex items-center justify-center mx-auto mb-4">
              <i className="ri-search-line text-3xl text-[#FF6B6B]"></i>
            </div>
            <h3 className="font-bold text-[#1A2332] text-lg mb-2">SEO Optimized</h3>
            <p className="text-gray-600 text-sm">Rank higher on Google</p>
          </div>

          <div className="bg-white/95 backdrop-blur-sm p-6 rounded-xl shadow-xl">
            <div className="w-14 h-14 bg-[#FF6B6B]/10 rounded-lg flex items-center justify-center mx-auto mb-4">
              <i className="ri-time-line text-3xl text-[#FF6B6B]"></i>
            </div>
            <h3 className="font-bold text-[#1A2332] text-lg mb-2">Fast Delivery</h3>
            <p className="text-gray-600 text-sm">Live in 2-3 weeks</p>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-10">
        <div className="animate-bounce">
          <i className="ri-arrow-down-line text-3xl text-white"></i>
        </div>
      </div>
    </section>
  );
}
